import {
  FIRESTORE_INVOICES_TABLE,
  FIRESTORE_ORDERS_TABLE,
  FIRESTORE_REGIONS_TABLE,
} from "../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../constants/CompanyConstants";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { firestore } from "../configs/FirebaseConfig";

// Enable dayjs week of year plugin
dayjs.extend(weekOfYear);

/**
 * Check if an invoice has a discount price mismatch
 * Returns true if customer has 100% discount but prices are not all zero
 * @param {Object} invoice - The invoice or order object
 * @returns {boolean} - True if mismatch detected
 */
export const hasDiscountPriceMismatch = (invoice) => {
  const customerDiscount = invoice?.customerDiscount || 0;
  if (customerDiscount !== 100) return false;

  const totalAmount = invoice?.totalAmount || 0;
  const subTotalAmount = invoice?.subTotalAmount || 0;

  // Check if totalAmount or subTotalAmount are not zero
  const hasNonZeroTotals =
    (totalAmount !== 0 && totalAmount !== "0" && totalAmount !== "0.00") ||
    (subTotalAmount !== 0 && subTotalAmount !== "0" && subTotalAmount !== "0.00");

  // Check if any product has non-zero price
  const hasNonZeroProductPrice = invoice?.userShoppingCart?.some((item) => {
    const price = parseFloat(item?.productPrice);
    return !isNaN(price) && price !== 0;
  });

  return hasNonZeroTotals || hasNonZeroProductPrice;
};

/**
 * Create a draft invoice when an order is marked as packed
 * @param {Object} order - The order object
 * @param {Object} profile - The profile object
 * @returns {Promise} - Promise that resolves when invoice is created
 */
export const createDraftInvoice = async (order, profile) => {
  try {
    const invoiceData = {
      farmorderIds: [order.farmorderId], // Array because invoice can contain multiple orders
      tax: order.tax || 0,
      subTotalWithoutDiscount: order.subTotalWithoutDiscount || order.subTotalAmount,
      paymentMethod: order.paymentMethod,
      invoiceAdditionalCosts: order.invoiceAdditionalCosts || 0,
      shippingCost: order.shippingCost || "0",
      customerDiscount: order.customerDiscount || 0,
      farmorderRegion: order.farmorderRegion,
      subTotalAmount: order.subTotalAmount,
      totalAmount: order.totalAmount,
      userShoppingCart: order.userShoppingCart.filter((item) => item.itemCount > 0),
      farmorderCustomerName: order.farmorderCustomerName,
      farmorderCustomerId: order.farmorderCustomerId,
      farmorderDeliveryDate: order.farmorderDeliveryDate,
      orderDateTime: order.orderDateTime,
      status: "draft",
      createdAt: dayjs().unix(),
      // createdBy: profile.userEmail,
      invoiceDate: dayjs().format("YYYY-MM-DD"),
      farmorderUserFirstName: order.farmorderUserFirstName,
      farmorderUserLastName: order.farmorderUserLastName,
      farmorderUserId: order.farmorderUserId,
      discountCodeAmount: order.discountCodeAmount || 0,
      discountCodePercentage: order.discountCodePercentage || 0,
      discountCodeApplied: order.discountCodeApplied || false,
      deliveryLabel: order.deliveryLabel,
      reference: order.reference || "",
    };

    // Generate invoice document
    const invoiceRef = doc(
      collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_INVOICES_TABLE),
    );

    await setDoc(invoiceRef, invoiceData);

    return invoiceRef.id;
  } catch (error) {
    console.error("Error creating draft invoice:", error);
    throw error;
  }
};

/**
 * Delete a draft invoice when an order is unpacked
 * @param {string} orderId - The order ID
 * @returns {Promise} - Promise that resolves when invoice is deleted
 */
export const deleteDraftInvoice = async (orderId) => {
  try {
    // Find the invoice that contains this order ID and has draft status
    const invoicesCollectionRef = collection(
      firestore,
      FIRESTORE_REGIONS_TABLE,
      COMPANY_COUNTRY_CODE,
      FIRESTORE_INVOICES_TABLE,
    );

    const q = query(
      invoicesCollectionRef,
      where("farmorderIds", "array-contains", orderId),
      where("status", "==", "draft"),
    );

    const invoicesSnapshot = await getDocs(q);

    if (!invoicesSnapshot.empty) {
      // Delete all draft invoices containing this order
      const deletePromises = invoicesSnapshot.docs.map((docSnapshot) => deleteDoc(docSnapshot.ref));
      await Promise.all(deletePromises);
    }
  } catch (error) {
    console.error("Error deleting draft invoice:", error);
    throw error;
  }
};

/**
 * Check if an order can be unpacked (invoice is not completed or combined)
 * @param {string} orderId - The order ID
 * @returns {Promise<boolean>} - Returns true if order can be unpacked
 */
export const canUnpackOrder = async (orderId) => {
  try {
    // Check if there's a completed or combined invoice for this order
    const invoicesCollectionRef = collection(
      firestore,
      FIRESTORE_REGIONS_TABLE,
      COMPANY_COUNTRY_CODE,
      FIRESTORE_INVOICES_TABLE,
    );

    // Check for completed invoices
    const completedQuery = query(
      invoicesCollectionRef,
      where("farmorderIds", "array-contains", orderId),
      where("status", "==", "in_exact"),
    );

    const completedSnapshot = await getDocs(completedQuery);
    if (!completedSnapshot.empty) {
      completedSnapshot.forEach((doc) => {});
      return false; // Cannot unpack if completed invoice exists
    }

    // Check for combined invoices
    const combinedQuery = query(
      invoicesCollectionRef,
      where("farmorderIds", "array-contains", orderId),
      where("status", "==", "combined"),
    );

    const combinedSnapshot = await getDocs(combinedQuery);
    if (!combinedSnapshot.empty) {
      combinedSnapshot.forEach((doc) => {});
      return false; // Cannot unpack if invoice is part of a combined invoice
    }

    return true; // Can unpack if no completed or combined invoices found
  } catch (error) {
    console.error("Error checking if order can be unpacked:", error);
    return false; // Default to not allowing unpack on error
  }
};

/**
 * Generate reference for combined invoice
 * Format: "W{week} {month} {year} | {n} orders"
 * Max 30 characters
 * @param {number} orderCount - Number of orders being combined
 * @returns {string} - Reference string (max 30 chars)
 */
export const generateCombinedInvoiceReference = (orderCount) => {
  const now = dayjs();
  const weekNumber = now.week();
  const month = now.format("MMM"); // Jan, Feb, etc.
  const year = now.format("YYYY");

  // Format: "W{week} {month} {year} | {n} orders"
  const reference = `W${weekNumber} ${month} ${year} | ${orderCount} orders`;

  // Safety check - should never exceed 30 with this format
  // Example: "W52 Dec 2024 | 99 orders" = 26 chars
  if (reference.length > 30) {
    console.warn(`Reference too long: ${reference} (${reference.length} chars)`);
    return reference.substring(0, 30);
  }

  return reference;
};

/**
 * Fetch multiple invoices by their IDs
 * @param {string[]} invoiceIds - Array of invoice IDs
 * @returns {Promise<Array>} - Array of invoice objects with their IDs
 */
const fetchInvoicesByIds = async (invoiceIds) => {
  const invoicesCollectionRef = collection(
    firestore,
    FIRESTORE_REGIONS_TABLE,
    COMPANY_COUNTRY_CODE,
    FIRESTORE_INVOICES_TABLE,
  );

  const fetchPromises = invoiceIds.map(async (id) => {
    const invoiceRef = doc(invoicesCollectionRef, id);
    const invoiceSnap = await getDoc(invoiceRef);

    if (!invoiceSnap.exists()) {
      throw new Error(`Invoice ${id} not found`);
    }

    return {
      id: invoiceSnap.id,
      ...invoiceSnap.data(),
    };
  });

  return Promise.all(fetchPromises);
};

/**
 * Geeft de facturen terug met een EDI-order erin (order met edifactOrderNumber).
 * Die mogen niet in een verzamelfactuur: Dekamarkt wil één factuur per order.
 */
export const getInvoicesWithEdiOrders = async (invoices) => {
  const farmorderIds = [...new Set(invoices.flatMap((inv) => inv.farmorderIds || []))];
  const ordersRef = collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERS_TABLE);
  // Firestore "in" accepteert maximaal 30 waarden per query.
  const chunks = [];
  for (let i = 0; i < farmorderIds.length; i += 30) chunks.push(farmorderIds.slice(i, i + 30));
  const snapshots = await Promise.all(chunks.map((ids) => getDocs(query(ordersRef, where("farmorderId", "in", ids)))));
  const ediFarmorderIds = new Set(
    snapshots
      .flatMap((snap) => snap.docs.map((d) => d.data()))
      .filter((o) => o.edifactOrderNumber)
      .map((o) => o.farmorderId),
  );
  return invoices.filter((inv) => (inv.farmorderIds || []).some((id) => ediFarmorderIds.has(id)));
};

/**
 * Combine multiple draft invoices into one combined invoice
 * Original invoices get status "combined" and are hidden from view
 *
 * @param {string[]} invoiceIds - Array of invoice IDs to combine
 * @param {string} referenceText - Reference text for combined invoice (max 30 chars)
 * @param {Object} profile - User profile object with userEmail
 * @returns {Promise<string>} - ID of the newly created combined invoice
 * @throws {Error} - If invoices are invalid for combining
 */
export const combineInvoices = async (invoiceIds, referenceText, profile) => {
  try {
    // 1. Validate input
    if (!invoiceIds || invoiceIds.length < 2) {
      throw new Error("At least 2 invoices are required to combine");
    }

    if (referenceText && referenceText.length > 30) {
      throw new Error("Reference text must be max 30 characters");
    }

    // 2. Fetch all invoices
    const invoices = await fetchInvoicesByIds(invoiceIds);

    // 3. Validate: all must be draft status
    const allDraft = invoices.every((inv) => inv.status === "draft");
    if (!allDraft) {
      const nonDraftInvoices = invoices.filter((inv) => inv.status !== "draft");
      throw new Error(
        `All invoices must have draft status. Non-draft invoices: ${nonDraftInvoices.map((i) => i.id).join(", ")}`,
      );
    }

    // 4. Validate: all must have same customer
    const customerIds = new Set(invoices.map((inv) => inv.farmorderCustomerId));
    if (customerIds.size > 1) {
      throw new Error(
        `All invoices must be for the same customer. Found customers: ${Array.from(customerIds).join(", ")}`,
      );
    }

    // 5. Validate: no invoice can already be combined
    const alreadyCombined = invoices.filter((inv) => inv.status === "combined" || inv.isCombinedInvoice);
    if (alreadyCombined.length > 0) {
      throw new Error(
        `Cannot combine invoices that are already part of a combined invoice: ${alreadyCombined
          .map((i) => i.id)
          .join(", ")}`,
      );
    }

    // 6. Validate: geen EDI-orders (één factuur per order, zie getInvoicesWithEdiOrders)
    const ediInvoices = await getInvoicesWithEdiOrders(invoices);
    if (ediInvoices.length > 0) {
      throw new Error(`EDI orders cannot be part of a combined invoice: ${ediInvoices.map((i) => i.id).join(", ")}`);
    }

    // 7. Create combined invoice details from each invoice
    const combinedInvoiceDetails = invoices.map((inv) => ({
      originalInvoiceId: inv.id,
      orderId: inv.farmorderIds[0], // Primary order ID
      deliveryDate: inv.farmorderDeliveryDate,
      products: inv.userShoppingCart,
      subTotal: inv.subTotalAmount,
      shippingCost: parseFloat(inv.shippingCost || 0),
      reference: inv.reference || "",
      orderDateTime: inv.orderDateTime,
    }));

    // 8. Merge all data
    const allFarmorderIds = invoices.flatMap((inv) => inv.farmorderIds);

    // Merge duplicate products by productId
    const productsMap = new Map();
    invoices.forEach((inv) => {
      inv.userShoppingCart.forEach((product) => {
        const existing = productsMap.get(product.productId);
        if (existing) {
          // Same product exists - add quantities together
          existing.itemCount += product.itemCount;
        } else {
          // New product - add to map
          productsMap.set(product.productId, { ...product });
        }
      });
    });
    const allProducts = Array.from(productsMap.values());

    const totalSubTotal = invoices.reduce((sum, inv) => sum + (inv.subTotalAmount || 0), 0);
    const totalShipping = invoices.reduce((sum, inv) => sum + parseFloat(inv.shippingCost || 0), 0);
    const totalAmount = invoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);

    // Use first invoice as template for customer data
    const firstInvoice = invoices[0];

    // 8. Create combined invoice data
    const combinedData = {
      // Combined invoice flags
      isCombinedInvoice: true,
      combinedFromInvoiceIds: invoiceIds,
      combinedInvoiceDetails,

      // Order data
      farmorderIds: allFarmorderIds,
      userShoppingCart: allProducts,

      // Financial data
      subTotalAmount: totalSubTotal,
      totalAmount: totalAmount,
      shippingCost: totalShipping.toString(),
      tax: firstInvoice.tax || 0,
      customerDiscount: firstInvoice.customerDiscount || 0,

      // Customer data (from first invoice)
      farmorderCustomerId: firstInvoice.farmorderCustomerId,
      farmorderCustomerName: firstInvoice.farmorderCustomerName,
      farmorderRegion: firstInvoice.farmorderRegion || "NL",
      farmorderUserFirstName: firstInvoice.farmorderUserFirstName || "",
      farmorderUserLastName: firstInvoice.farmorderUserLastName || "",
      farmorderUserId: firstInvoice.farmorderUserId || "",

      // Use earliest delivery date as the invoice delivery date
      farmorderDeliveryDate: invoices.map((inv) => inv.farmorderDeliveryDate).sort()[0],

      // Use earliest order date time
      orderDateTime: Math.min(...invoices.map((inv) => inv.orderDateTime)),

      // Metadata
      status: "draft",
      reference: referenceText || generateCombinedInvoiceReference(invoices.length),
      invoiceDate: dayjs().format("YYYY-MM-DD"),
      createdAt: dayjs().unix(),
      createdBy: profile?.userEmail || "system",

      // Optional fields from first invoice (with fallbacks)
      paymentMethod: firstInvoice.paymentMethod || "",
      deliveryLabel: firstInvoice.deliveryLabel || "",
      discountCodeAmount: 0, // Reset for combined invoice
      discountCodePercentage: 0,
      discountCodeApplied: false,
    };

    // 9. Create combined invoice in Firestore
    const combinedInvoiceRef = doc(
      collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_INVOICES_TABLE),
    );
    await setDoc(combinedInvoiceRef, combinedData);

    // 10. Update original invoices to "combined" status
    const updatePromises = invoices.map((inv) => {
      const invoiceRef = doc(
        firestore,
        FIRESTORE_REGIONS_TABLE,
        COMPANY_COUNTRY_CODE,
        FIRESTORE_INVOICES_TABLE,
        inv.id,
      );

      return updateDoc(invoiceRef, {
        status: "combined",
        combinedIntoInvoiceId: combinedInvoiceRef.id,
        combinedAt: dayjs().unix(),
        combinedBy: profile?.userEmail || "system",
        updatedAt: dayjs().unix(),
        updatedBy: profile?.userEmail || "system",
      });
    });
    await Promise.all(updatePromises);
    return combinedInvoiceRef.id;
  } catch (error) {
    console.error("Error combining invoices:", error);
    throw error;
  }
};

/**
 * Split a combined invoice back to its original invoices
 * Resets original invoices back to "draft" status
 * Only works if combined invoice is still in draft status
 *
 * @param {string} combinedInvoiceId - ID of the combined invoice to split
 * @returns {Promise<string[]>} - Array of original invoice IDs that were restored
 * @throws {Error} - If invoice cannot be split (not combined, not draft, etc.)
 */
export const splitCombinedInvoice = async (combinedInvoiceId) => {
  try {
    // 1. Fetch combined invoice
    const combinedInvoiceRef = doc(
      firestore,
      FIRESTORE_REGIONS_TABLE,
      COMPANY_COUNTRY_CODE,
      FIRESTORE_INVOICES_TABLE,
      combinedInvoiceId,
    );
    const combinedInvoiceSnap = await getDoc(combinedInvoiceRef);

    if (!combinedInvoiceSnap.exists()) {
      throw new Error(`Invoice ${combinedInvoiceId} not found`);
    }

    const combinedInvoice = combinedInvoiceSnap.data();

    // 2. Validate: must be a combined invoice
    if (!combinedInvoice.isCombinedInvoice) {
      throw new Error(`Invoice ${combinedInvoiceId} is not a combined invoice`);
    }

    // 3. Validate: must still be in draft status
    if (combinedInvoice.status !== "draft") {
      throw new Error(`Can only split draft invoices. Current status: ${combinedInvoice.status}`);
    }

    // 4. Get original invoice IDs
    const originalInvoiceIds = combinedInvoice.combinedFromInvoiceIds;
    if (!originalInvoiceIds || originalInvoiceIds.length === 0) {
      throw new Error("No original invoice IDs found in combined invoice");
    }

    // 5. Reset each original invoice back to "draft" status
    const updatePromises = originalInvoiceIds.map(async (invId) => {
      const invoiceRef = doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_INVOICES_TABLE, invId);

      // Verify invoice exists and has correct status
      const invoiceSnap = await getDoc(invoiceRef);
      if (!invoiceSnap.exists()) {
        console.warn(`Original invoice ${invId} not found, skipping`);
        return null;
      }

      const invoiceData = invoiceSnap.data();
      if (invoiceData.status !== "combined") {
        console.warn(`Original invoice ${invId} has unexpected status: ${invoiceData.status}, skipping`);
        return null;
      }

      // Reset to draft and remove combined metadata
      return updateDoc(invoiceRef, {
        status: "draft",
        combinedIntoInvoiceId: deleteField(),
        combinedAt: deleteField(),
        combinedBy: deleteField(),
        updatedAt: dayjs().unix(),
        updatedBy: "system",
      });
    });

    // 6. Delete the combined invoice
    await deleteDoc(combinedInvoiceRef);

    return originalInvoiceIds;
  } catch (error) {
    console.error("Error splitting combined invoice:", error);
    throw error;
  }
};
